import React, { useState, useEffect } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { 
  TooltipComponent, 
  LegendComponent, 
  GridComponent,
  DataZoomComponent,
  ToolboxComponent 
} from 'echarts/components';
import { 
  LineChart, 
  BarChart, 
  PieChart, 
  ScatterChart 
} from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import axios from 'axios';
import { Spinner, Card, Row, Col, Button, ButtonGroup, Badge, Alert } from 'react-bootstrap';
import dayjs from 'dayjs';
import { useUserContext } from '../../../../layouts/UserContext';
import { getHatateToken } from "../../../../helpers/utils";

// Register ECharts components
echarts.use([
  TooltipComponent, 
  LegendComponent, 
  GridComponent, 
  DataZoomComponent,
  ToolboxComponent,
  LineChart, 
  BarChart, 
  PieChart, 
  ScatterChart, 
  CanvasRenderer
]);

type LanguageType = 'es' | 'en';

interface ConversationStat {
  contactId: string;
  contactName: string;
  date: string;
  messageCount: number;
}

interface LocationData {
  sender: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  isActive: boolean;
}

interface WazeData {
  sender: string;
  wazeUrl: string;
  timestamp: string;
}

interface ImageData {
  sender: string;
  timestamp: string;
  processed: boolean;
}

interface BotStatus {
  botEnabled: boolean;
  dynamicFlowsEnabled: boolean;
  copilotEnabled: boolean;
  dynamicFlowsCount: number;
}

interface DashboardData {
  messageStats: ConversationStat[];
  conversations: any[];
  locations: LocationData[];
  wazeLinks: WazeData[];
  images: ImageData[];
  botStatus: BotStatus;
}

interface WhatsAppAnalyticsDashboardProps {
  language: LanguageType;
  days?: number;
}

const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/';
const API_KEY = 'anubis-whatsapp-api-key';

const WhatsAppAnalyticsDashboard: React.FC<WhatsAppAnalyticsDashboardProps> = ({
  language,
  days = 7
}) => {
  const { user } = useUserContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [activeChart, setActiveChart] = useState<string>('overview');
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>('');

  const whatsAppServerPort = user?.mlStuff?.whatsAppServerPort;
  const whatsAppApiPort = user?.mlStuff?.whatsAppApiPort;
  const mlServerPort = user?.mlStuff?.mlServerPort;

  useEffect(() => {
    fetchAllData(true); // Initial load
    
    // Auto-refresh every 30 seconds (silent updates)
    const interval = setInterval(() => fetchAllData(false), 30000);
    setRefreshInterval(interval);
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [days]);

  const fetchAllData = async (isInitial: boolean = false) => {
    try {
      // Only show loading spinner on initial load
      if (isInitial) {
        setLoading(true);
        setIsInitialLoad(true);
      } else {
        setIsRefreshing(true);
      }
      
      const currentToken = getHatateToken();
      
      const headers = {
        'x-api-key': API_KEY,
        'Authorization': `${currentToken}`,
        'Content-Type': 'application/json'
      };

      const requests = [
        // Message statistics
        axios.get(`${API_BASE_URL}/bot/message-stats`, {
          headers,
          params: { days }
        }),
        
        // Conversations list
        axios.get(`${API_BASE_URL}/bot/conversations`, {
          headers,
          params: { limit: 50 }
        }),
        
        // Active locations
        axios.get(`${API_BASE_URL}/locations/active`, {
          headers
        }),
        
        // Waze links
        axios.get(`${API_BASE_URL}/bot/waze-links`, {
          headers,
          params: { limit: 100 }
        }),
        
        // Image history
        axios.get(`${API_BASE_URL}/bot/image-history`, {
          headers,
          params: { limit: 100 }
        }),
        
        // Bot status
        axios.get(`${API_BASE_URL}/bot/status`, {
          headers
        })
      ];

      const responses = await Promise.allSettled(requests);
      
      const data: DashboardData = {
        messageStats: responses[0].status === 'fulfilled' && responses[0].value.data.success 
          ? responses[0].value.data.stats || [] 
          : [],
        conversations: responses[1].status === 'fulfilled' && responses[1].value.data.success 
          ? responses[1].value.data.conversations || [] 
          : [],
        locations: responses[2].status === 'fulfilled' && responses[2].value.data.success 
          ? responses[2].value.data.locations || [] 
          : [],
        wazeLinks: responses[3].status === 'fulfilled' && responses[3].value.data.success 
          ? responses[3].value.data.links || [] 
          : [],
        images: responses[4].status === 'fulfilled' && responses[4].value.data.success 
          ? responses[4].value.data.images || [] 
          : [],
        botStatus: responses[5].status === 'fulfilled' && responses[5].value.data.success 
          ? {
              botEnabled: responses[5].value.data.botEnabled || false,
              dynamicFlowsEnabled: responses[5].value.data.dynamicFlowsEnabled || false,
              copilotEnabled: responses[5].value.data.copilotEnabled || false,
              dynamicFlowsCount: responses[5].value.data.dynamicFlowsCount || 0
            }
          : {
              botEnabled: false,
              dynamicFlowsEnabled: false,
              copilotEnabled: false,
              dynamicFlowsCount: 0
            }
      };

      setDashboardData(data);
      setError(null);
      setLastUpdate(dayjs().format('HH:mm:ss'));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Only set error on initial load to avoid disrupting user experience
      if (isInitial) {
        setError(language === 'es' 
          ? 'Error al cargar los datos del dashboard' 
          : 'Error loading dashboard data');
      }
    } finally {
      if (isInitial) {
        setLoading(false);
        setIsInitialLoad(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  const getOverviewChartOptions = () => {
    if (!dashboardData) return {};

    const totalMessages = dashboardData.messageStats.reduce((sum, stat) => sum + stat.messageCount, 0);
    const totalContacts = new Set(dashboardData.messageStats.map(stat => stat.contactId)).size;
    const activeLocations = dashboardData.locations.filter(loc => loc.isActive).length;
    const totalImages = dashboardData.images.length;
    const totalWazeLinks = dashboardData.wazeLinks.length;

    return {
      title: {
        text: language === 'es' ? 'Resumen General' : 'General Overview',
        left: 'center',
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold'
        }
      },
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/>{b}: {c} ({d}%)'
      },
      legend: {
        bottom: '5%',
        data: [
          language === 'es' ? 'Mensajes' : 'Messages',
          language === 'es' ? 'Contactos' : 'Contacts',
          language === 'es' ? 'Ubicaciones Activas' : 'Active Locations',
          language === 'es' ? 'Imágenes' : 'Images',
          language === 'es' ? 'Enlaces Waze' : 'Waze Links'
        ]
      },
      series: [
        {
          name: language === 'es' ? 'Actividad WhatsApp' : 'WhatsApp Activity',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '50%'],
          data: [
            { 
              value: totalMessages, 
              name: language === 'es' ? 'Mensajes' : 'Messages',
              itemStyle: { color: '#4CAF50' }
            },
            { 
              value: totalContacts, 
              name: language === 'es' ? 'Contactos' : 'Contacts',
              itemStyle: { color: '#2196F3' }
            },
            { 
              value: activeLocations, 
              name: language === 'es' ? 'Ubicaciones Activas' : 'Active Locations',
              itemStyle: { color: '#FF9800' }
            },
            { 
              value: totalImages, 
              name: language === 'es' ? 'Imágenes' : 'Images',
              itemStyle: { color: '#9C27B0' }
            },
            { 
              value: totalWazeLinks, 
              name: language === 'es' ? 'Enlaces Waze' : 'Waze Links',
              itemStyle: { color: '#E91E63' }
            }
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ]
    };
  };

  const getMessageEvolutionChartOptions = () => {
    if (!dashboardData || !dashboardData.messageStats.length) return {};

    // Group messages by date
    const messagesByDate = dashboardData.messageStats.reduce((acc, stat) => {
      const date = dayjs(stat.date).format('YYYY-MM-DD');
      if (!acc[date]) acc[date] = 0;
      acc[date] += stat.messageCount;
      return acc;
    }, {} as Record<string, number>);

    const dates = Object.keys(messagesByDate).sort();
    const values = dates.map(date => messagesByDate[date]);

    return {
      title: {
        text: language === 'es' ? 'Evolución de Mensajes por Día' : 'Message Evolution by Day',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        formatter: function(params: any) {
          const date = dayjs(params[0].name).format('DD MMM YYYY');
          return `${date}<br/>${params[0].marker} ${params[0].seriesName}: ${params[0].value}`;
        }
      },
      xAxis: {
        type: 'category',
        data: dates.map(date => dayjs(date).format('DD/MM')),
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: {
        type: 'value',
        name: language === 'es' ? 'Mensajes' : 'Messages'
      },
      series: [
        {
          name: language === 'es' ? 'Mensajes' : 'Messages',
          type: 'line',
          data: values,
          smooth: true,
          areaStyle: {
            opacity: 0.3
          },
          itemStyle: {
            color: '#4CAF50'
          }
        }
      ]
    };
  };

  const getContactActivityChartOptions = () => {
    if (!dashboardData || !dashboardData.messageStats.length) return {};

    // Group by contact and sum messages
    const contactActivity = dashboardData.messageStats.reduce((acc, stat) => {
      const contactName = stat.contactName || stat.contactId.substring(0, 10);
      if (!acc[contactName]) acc[contactName] = 0;
      acc[contactName] += stat.messageCount;
      return acc;
    }, {} as Record<string, number>);

    const contacts = Object.keys(contactActivity).slice(0, 15); // Top 15 contacts
    const values = contacts.map(contact => contactActivity[contact]);

    return {
      title: {
        text: language === 'es' ? 'Actividad por Contacto (Top 15)' : 'Activity by Contact (Top 15)',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      xAxis: {
        type: 'value',
        name: language === 'es' ? 'Mensajes' : 'Messages'
      },
      yAxis: {
        type: 'category',
        data: contacts,
        axisLabel: {
          width: 100,
          overflow: 'truncate'
        }
      },
      series: [
        {
          name: language === 'es' ? 'Mensajes' : 'Messages',
          type: 'bar',
          data: values,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#4CAF50' },
              { offset: 1, color: '#8BC34A' }
            ])
          }
        }
      ]
    };
  };

  const getHourlyActivityChartOptions = () => {
    if (!dashboardData || !dashboardData.messageStats.length) return {};

    // Group by hour
    const hourlyActivity = Array.from({ length: 24 }, () => 0);
    
    dashboardData.messageStats.forEach(stat => {
      const hour = dayjs(stat.date).hour();
      hourlyActivity[hour] += stat.messageCount;
    });

    return {
      title: {
        text: language === 'es' ? 'Actividad por Hora del Día' : 'Activity by Hour of Day',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        formatter: function(params: any) {
          return `${params[0].name}:00<br/>${params[0].marker} ${params[0].seriesName}: ${params[0].value}`;
        }
      },
      xAxis: {
        type: 'category',
        data: Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')),
        name: language === 'es' ? 'Hora' : 'Hour'
      },
      yAxis: {
        type: 'value',
        name: language === 'es' ? 'Mensajes' : 'Messages'
      },
      series: [
        {
          name: language === 'es' ? 'Mensajes' : 'Messages',
          type: 'bar',
          data: hourlyActivity,
          itemStyle: {
            color: function(params: any) {
              const colors = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0'];
              return colors[params.dataIndex % colors.length];
            }
          }
        }
      ]
    };
  };

  const getLocationHeatmapOptions = () => {
    if (!dashboardData || !dashboardData.locations.length) return {};

    const locationData = dashboardData.locations.map((loc, index) => [
      loc.longitude,
      loc.latitude,
      1,
      loc.sender
    ]);

    return {
      title: {
        text: language === 'es' ? 'Mapa de Ubicaciones' : 'Location Map',
        left: 'center'
      },
      tooltip: {
        trigger: 'item',
        formatter: function(params: any) {
          return `${params.data[3]}<br/>Lat: ${params.data[1]}<br/>Lng: ${params.data[0]}`;
        }
      },
      xAxis: {
        type: 'value',
        name: language === 'es' ? 'Longitud' : 'Longitude'
      },
      yAxis: {
        type: 'value',
        name: language === 'es' ? 'Latitud' : 'Latitude'
      },
      series: [
        {
          name: language === 'es' ? 'Ubicaciones' : 'Locations',
          type: 'scatter',
          data: locationData,
          symbolSize: function(data: any) {
            return Math.max(data[2] * 20, 8);
          },
          itemStyle: {
            color: '#FF5722',
            opacity: 0.8
          },
          emphasis: {
            itemStyle: {
              color: '#FF9800'
            }
          }
        }
      ]
    };
  };

  interface TimelineItem {
    time: number;
    type: 'message' | 'location' | 'image';
    count?: number;
    contact: string;
  }

  const getTimelineChartOptions = () => {
    if (!dashboardData) return {};

    const now = dayjs();
    const timelineData: TimelineItem[] = [];

    // Add message events
    dashboardData.messageStats.forEach(stat => {
      timelineData.push({
        time: dayjs(stat.date).valueOf(),
        type: 'message',
        count: stat.messageCount,
        contact: stat.contactName
      });
    });

    // Add location events
    dashboardData.locations.forEach(loc => {
      timelineData.push({
        time: dayjs(loc.timestamp).valueOf(),
        type: 'location',
        contact: loc.sender
      });
    });

    // Add image events
    dashboardData.images.forEach(img => {
      timelineData.push({
        time: dayjs(img.timestamp).valueOf(),
        type: 'image',
        contact: img.sender
      });
    });

    timelineData.sort((a, b) => a.time - b.time);

    const hours = timelineData.map(item => dayjs(item.time).format('HH:mm'));
    const messageData = timelineData.map(item => item.type === 'message' ? item.count || 1 : 0);
    const locationData = timelineData.map(item => item.type === 'location' ? 1 : 0);
    const imageData = timelineData.map(item => item.type === 'image' ? 1 : 0);

    return {
      title: {
        text: language === 'es' ? 'Línea de Tiempo de Actividad' : 'Activity Timeline',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        bottom: '5%',
        data: [
          language === 'es' ? 'Mensajes' : 'Messages',
          language === 'es' ? 'Ubicaciones' : 'Locations',
          language === 'es' ? 'Imágenes' : 'Images'
        ]
      },
      xAxis: {
        type: 'category',
        data: hours,
        axisLabel: {
          rotate: 45
        }
      },
      yAxis: {
        type: 'value'
      },
      series: [
        {
          name: language === 'es' ? 'Mensajes' : 'Messages',
          type: 'line',
          data: messageData,
          itemStyle: { color: '#4CAF50' }
        },
        {
          name: language === 'es' ? 'Ubicaciones' : 'Locations',
          type: 'line',
          data: locationData,
          itemStyle: { color: '#FF9800' }
        },
        {
          name: language === 'es' ? 'Imágenes' : 'Images',
          type: 'line',
          data: imageData,
          itemStyle: { color: '#9C27B0' }
        }
      ]
    };
  };

  const renderChart = () => {
    if (!dashboardData) return null;

    let options = {};
    
    switch (activeChart) {
      case 'overview':
        options = getOverviewChartOptions();
        break;
      case 'evolution':
        options = getMessageEvolutionChartOptions();
        break;
      case 'contacts':
        options = getContactActivityChartOptions();
        break;
      case 'hourly':
        options = getHourlyActivityChartOptions();
        break;
      case 'locations':
        options = getLocationHeatmapOptions();
        break;
      case 'timeline':
        options = getTimelineChartOptions();
        break;
      default:
        options = getOverviewChartOptions();
    }

    return (
      <ReactEChartsCore
        echarts={echarts}
        option={options}
        style={{ height: '500px', width: '100%' }}
        notMerge={true}
        lazyUpdate={true}
      />
    );
  };

  const getStatusCards = () => {
    if (!dashboardData) return null;

    const totalMessages = dashboardData.messageStats.reduce((sum, stat) => sum + stat.messageCount, 0);
    const totalContacts = new Set(dashboardData.messageStats.map(stat => stat.contactId)).size;
    const activeLocations = dashboardData.locations.filter(loc => loc.isActive).length;
    const totalConversations = dashboardData.conversations.length;

    return (
      <Row className="mb-4">
        <Col xs={12} sm={6} lg={3} className="mb-3 mb-lg-0">
          <Card className="text-center border-0 shadow-sm h-100">
            <Card.Body>
              <h2 className="text-success">{totalMessages}</h2>
              <p className="text-muted mb-0">
                {language === 'es' ? 'Total Mensajes' : 'Total Messages'}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3 mb-lg-0">
          <Card className="text-center border-0 shadow-sm h-100">
            <Card.Body>
              <h2 className="text-primary">{totalContacts}</h2>
              <p className="text-muted mb-0">
                {language === 'es' ? 'Contactos Activos' : 'Active Contacts'}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3 mb-sm-0">
          <Card className="text-center border-0 shadow-sm h-100">
            <Card.Body>
              <h2 className="text-warning">{activeLocations}</h2>
              <p className="text-muted mb-0">
                {language === 'es' ? 'Ubicaciones en Vivo' : 'Live Locations'}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="text-center border-0 shadow-sm h-100">
            <Card.Body>
              <h2 className="text-info">{totalConversations}</h2>
              <p className="text-muted mb-0">
                {language === 'es' ? 'Conversaciones' : 'Conversations'}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const getBotStatusBadges = () => {
    if (!dashboardData) return null;

    return (
      <Row className="mb-3">
        <Col xs={12}>
          <div className="d-flex flex-wrap gap-2">
            <Badge 
              bg={dashboardData.botStatus.botEnabled ? 'success' : 'danger'} 
              className="px-2 py-1"
            >
              {language === 'es' ? 'Bot' : 'Bot'}: {dashboardData.botStatus.botEnabled ? 'ON' : 'OFF'}
            </Badge>
            <Badge 
              bg={dashboardData.botStatus.dynamicFlowsEnabled ? 'success' : 'secondary'} 
              className="px-2 py-1"
            >
              {language === 'es' ? 'Flujos Dinámicos' : 'Dynamic Flows'}: {dashboardData.botStatus.dynamicFlowsEnabled ? 'ON' : 'OFF'}
            </Badge>
            <Badge 
              bg={dashboardData.botStatus.copilotEnabled ? 'success' : 'secondary'} 
              className="px-2 py-1"
            >
              CoPilot: {dashboardData.botStatus.copilotEnabled ? 'ON' : 'OFF'}
            </Badge>
            <Badge bg="info" className="px-2 py-1">
              {language === 'es' ? 'Flujos' : 'Flows'}: {dashboardData.botStatus.dynamicFlowsCount}
            </Badge>
          </div>
        </Col>
      </Row>
    );
  };

  // Only show loading spinner on initial load
  if (loading && isInitialLoad) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
        <p className="mt-3">{language === 'es' ? 'Cargando dashboard...' : 'Loading dashboard...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <i className="fas fa-exclamation-circle me-2"></i>
        {error}
        <Button 
          variant="outline-danger" 
          size="sm" 
          className="ms-2"
          onClick={() => fetchAllData(true)}
        >
          {language === 'es' ? 'Reintentar' : 'Retry'}
        </Button>
      </Alert>
    );
  }

  if (!dashboardData) {
    return (
      <Alert variant="info">
        <i className="fas fa-info-circle me-2"></i>
        {language === 'es' 
          ? 'No hay datos disponibles para mostrar.' 
          : 'No data available to display.'}
      </Alert>
    );
  }

  return (
    <div className="whatsapp-analytics-dashboard">
      <Row className="mb-4">
        <Col xs={12} lg={8}>
          <div>
            <h4 className="mb-1">
              {language === 'es' ? 'Dashboard de WhatsApp Analytics' : 'WhatsApp Analytics Dashboard'}
            </h4>
            <div className="d-flex align-items-center flex-wrap">
              <small className="text-muted me-2">
                {language === 'es' ? 'Actualizado:' : 'Updated:'} {lastUpdate}
              </small>
              {isRefreshing && (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-1" />
                  <small className="text-muted">
                    {language === 'es' ? 'Actualizando...' : 'Updating...'}
                  </small>
                </div>
              )}
            </div>
          </div>
        </Col>
        <Col xs={12} lg={4} className="mt-2 mt-lg-0">
          <div className="d-flex justify-content-lg-end">
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => fetchAllData(false)}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  {language === 'es' ? 'Actualizando...' : 'Updating...'}
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt me-1"></i>
                  {language === 'es' ? 'Actualizar' : 'Refresh'}
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>

      {getBotStatusBadges()}
      {getStatusCards()}

      <Card className="shadow-sm">
        <Card.Header>
          <Row className="align-items-center">
            <Col xs={12} xl={12} lg={12} sm={12} className="mb-2 mb-xl-0">
              <h5 className="mb-0">
                {language === 'es' ? 'Análisis Visual' : 'Visual Analytics'}
              </h5>
            </Col>
            <Col xs={12} xl={12} lg={12} sm={12}>
              <div className="d-flex justify-content-xl-end">
                <div className="btn-group-vertical btn-group-sm d-block d-md-none w-100">
                  <Button 
                    variant={activeChart === 'overview' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('overview')}
                    size="sm"
                  >
                    {language === 'es' ? 'Resumen' : 'Overview'}
                  </Button>
                  <Button 
                    variant={activeChart === 'evolution' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('evolution')}
                    size="sm"
                  >
                    {language === 'es' ? 'Evolución' : 'Evolution'}
                  </Button>
                  <Button 
                    variant={activeChart === 'contacts' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('contacts')}
                    size="sm"
                  >
                    {language === 'es' ? 'Contactos' : 'Contacts'}
                  </Button>
                  <Button 
                    variant={activeChart === 'hourly' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('hourly')}
                    size="sm"
                  >
                    {language === 'es' ? 'Por Hora' : 'Hourly'}
                  </Button>
                  <Button 
                    variant={activeChart === 'locations' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('locations')}
                    size="sm"
                  >
                    {language === 'es' ? 'Ubicaciones' : 'Locations'}
                  </Button>
                  <Button 
                    variant={activeChart === 'timeline' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('timeline')}
                    size="sm"
                  >
                    {language === 'es' ? 'Timeline' : 'Timeline'}
                  </Button>
                </div>
                <ButtonGroup size="sm" className="d-none d-md-flex flex-wrap">
                  <Button 
                    variant={activeChart === 'overview' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('overview')}
                  >
                    {language === 'es' ? 'Resumen' : 'Overview'}
                  </Button>
                  <Button 
                    variant={activeChart === 'evolution' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('evolution')}
                  >
                    {language === 'es' ? 'Evolución' : 'Evolution'}
                  </Button>
                  <Button 
                    variant={activeChart === 'contacts' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('contacts')}
                  >
                    {language === 'es' ? 'Contactos' : 'Contacts'}
                  </Button>
                  <Button 
                    variant={activeChart === 'hourly' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('hourly')}
                  >
                    {language === 'es' ? 'Por Hora' : 'Hourly'}
                  </Button>
                  <Button 
                    variant={activeChart === 'locations' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('locations')}
                  >
                    {language === 'es' ? 'Ubicaciones' : 'Locations'}
                  </Button>
                  <Button 
                    variant={activeChart === 'timeline' ? 'primary' : 'outline-primary'}
                    onClick={() => setActiveChart('timeline')}
                  >
                    {language === 'es' ? 'Timeline' : 'Timeline'}
                  </Button>
                </ButtonGroup>
              </div>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {renderChart()}
        </Card.Body>
      </Card>

      <Row className="mt-4">
        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">
                {language === 'es' ? 'Conversaciones Recientes' : 'Recent Conversations'}
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="conversation-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {dashboardData.conversations.slice(0, 5).map((conv, index) => (
                  <div key={index} className="d-flex justify-content-between py-2 border-bottom">
                    <div className="flex-grow-1 me-2">
                      <strong className="d-block text-truncate">{conv.name}</strong>
                      <small className="text-muted d-block text-truncate">{conv.preview}</small>
                    </div>
                    <div className="text-end flex-shrink-0">
                      <Badge bg="secondary">{conv.messageCount}</Badge>
                      <br />
                      <small className="text-muted">
                        {dayjs(conv.lastMessageTime).format('DD/MM HH:mm')}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">
                {language === 'es' ? 'Actividad Reciente' : 'Recent Activity'}
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="activity-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {[
                  ...dashboardData.locations.slice(0, 2).map(loc => ({
                    type: 'location',
                    timestamp: loc.timestamp,
                    text: `${language === 'es' ? 'Ubicación compartida por' : 'Location shared by'} ${loc.sender.substring(0, 10)}...`
                  })),
                  ...dashboardData.images.slice(0, 2).map(img => ({
                    type: 'image',
                    timestamp: img.timestamp,
                    text: `${language === 'es' ? 'Imagen recibida de' : 'Image received from'} ${img.sender.substring(0, 10)}...`
                  })),
                  ...dashboardData.wazeLinks.slice(0, 1).map(waze => ({
                    type: 'waze',
                    timestamp: waze.timestamp,
                    text: `${language === 'es' ? 'Enlace Waze compartido por' : 'Waze link shared by'} ${waze.sender.substring(0, 10)}...`
                  }))
                ].sort((a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf())
                .slice(0, 5)
                .map((activity, index) => (
                  <div key={index} className="d-flex align-items-center py-2 border-bottom">
                    <div className="me-3 flex-shrink-0">
                      {activity.type === 'location' && (
                        <i className="fas fa-map-marker-alt text-warning"></i>
                      )}
                      {activity.type === 'image' && (
                        <i className="fas fa-image text-primary"></i>
                      )}
                      {activity.type === 'waze' && (
                        <i className="fas fa-route text-info"></i>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="small text-truncate">{activity.text}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {dayjs(activity.timestamp).format('DD/MM/YYYY HH:mm')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col xs={12} sm={6} lg={4} className="mb-4 mb-sm-0">
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">
                {language === 'es' ? 'Estadísticas de Imágenes' : 'Image Statistics'}
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <h3 className="text-primary">{dashboardData.images.length}</h3>
                <p className="text-muted mb-2">
                  {language === 'es' ? 'Total de imágenes' : 'Total images'}
                </p>
                <Row>
                  <Col xs={6}>
                    <div className="text-success">
                      {dashboardData.images.filter(img => img.processed).length}
                    </div>
                    <small className="text-muted">
                      {language === 'es' ? 'Procesadas' : 'Processed'}
                    </small>
                  </Col>
                  <Col xs={6}>
                    <div className="text-warning">
                      {dashboardData.images.filter(img => !img.processed).length}
                    </div>
                    <small className="text-muted">
                      {language === 'es' ? 'Pendientes' : 'Pending'}
                    </small>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={4} className="mb-4 mb-lg-0">
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">
                {language === 'es' ? 'Enlaces Waze' : 'Waze Links'}
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <h3 className="text-info">{dashboardData.wazeLinks.length}</h3>
                <p className="text-muted mb-2">
                  {language === 'es' ? 'Enlaces compartidos' : 'Shared links'}
                </p>
                {dashboardData.wazeLinks.length > 0 && (
                  <small className="text-muted">
                    {language === 'es' ? 'Último:' : 'Latest:'} {' '}
                    {dayjs(dashboardData.wazeLinks[0]?.timestamp).format('DD/MM HH:mm')}
                  </small>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={4}>
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">
                {language === 'es' ? 'Ubicaciones' : 'Locations'}
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                <h3 className="text-warning">
                  {dashboardData.locations.filter(loc => loc.isActive).length}
                </h3>
                <p className="text-muted mb-2">
                  {language === 'es' ? 'Activas ahora' : 'Active now'}
                </p>
                <Row>
                  <Col xs={12}>
                    <div className="text-secondary">
                      {dashboardData.locations.length}
                    </div>
                    <small className="text-muted">
                      {language === 'es' ? 'Total historial' : 'Total history'}
                    </small>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style>{`
        .whatsapp-analytics-dashboard .card {
          transition: all 0.3s ease;
        }
        
        .whatsapp-analytics-dashboard .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }
        
        .whatsapp-analytics-dashboard .btn-group .btn {
          border-radius: 0;
        }
        
        .whatsapp-analytics-dashboard .btn-group .btn:first-child {
          border-top-left-radius: 0.375rem;
          border-bottom-left-radius: 0.375rem;
        }
        
        .whatsapp-analytics-dashboard .btn-group .btn:last-child {
          border-top-right-radius: 0.375rem;
          border-bottom-right-radius: 0.375rem;
        }
        
        .whatsapp-analytics-dashboard .border-bottom:last-child {
          border-bottom: none !important;
        }
        
        .whatsapp-analytics-dashboard .badge {
          font-size: 0.75rem;
        }
        
        .whatsapp-analytics-dashboard .fas {
          width: 16px;
          text-align: center;
        }
        
        .whatsapp-analytics-dashboard .btn-group-vertical .btn {
          border-radius: 0.375rem !important;
          margin-bottom: 0.25rem;
        }
        
        .whatsapp-analytics-dashboard .btn-group-vertical .btn:last-child {
          margin-bottom: 0;
        }
        
        @media (max-width: 767.98px) {
          .whatsapp-analytics-dashboard .card-body {
            padding: 1rem 0.75rem;
          }
          
          .whatsapp-analytics-dashboard h2 {
            font-size: 1.5rem;
          }
          
          .whatsapp-analytics-dashboard h3 {
            font-size: 1.25rem;
          }
          
          .whatsapp-analytics-dashboard .btn-group-vertical {
            gap: 0.25rem;
          }
        }
        
        @media (max-width: 575.98px) {
          .whatsapp-analytics-dashboard .card-body {
            padding: 0.75rem 0.5rem;
          }
          
          .whatsapp-analytics-dashboard .text-truncate {
            max-width: 150px;
          }
        }
        
        .whatsapp-analytics-dashboard .conversation-list,
        .whatsapp-analytics-dashboard .activity-list {
          scrollbar-width: thin;
          scrollbar-color: #6c757d transparent;
        }
        
        .whatsapp-analytics-dashboard .conversation-list::-webkit-scrollbar,
        .whatsapp-analytics-dashboard .activity-list::-webkit-scrollbar {
          width: 6px;
        }
        
        .whatsapp-analytics-dashboard .conversation-list::-webkit-scrollbar-track,
        .whatsapp-analytics-dashboard .activity-list::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .whatsapp-analytics-dashboard .conversation-list::-webkit-scrollbar-thumb,
        .whatsapp-analytics-dashboard .activity-list::-webkit-scrollbar-thumb {
          background-color: #6c757d;
          border-radius: 3px;
        }
      `}</style>
    </div>
  );
};

export default WhatsAppAnalyticsDashboard;